import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Activity, Eye, Users, Lock, BarChart3, ChevronDown, ChevronRight, Clock, TrendingUp, HelpCircle } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { LiveVisitorsList } from "./LiveVisitorsList";

/**
 * PresenceStatsCard (iter179) — Two-panel presence dashboard.
 *
 *   1. LIVE buckets: now / 5m / 1h / 24h. Auto-refresh every 15s.
 *   2. TODAY (live) — full 24-hour breakdown that keeps ticking as the
 *      day unfolds. Each hour is a visible bar + a numbered cell so the
 *      operator sees which slot is busiest right now, not just at end
 *      of day. Auto-refresh every 30s.
 *   3. RECENT DAYS — condensed one-row-per-day history. Each row shows
 *      totals, busiest hour, and a tiny inline sparkline. Click a row
 *      to expand it into the same 24-slot chart as Today.
 *
 * Data sources: `GET /admin/presence/stats`, `/hourly?date=today`,
 * `/hourly/summary?days=14` (one call for all N rows). All three refresh
 * cheap enough to poll on the admin's tab.
 */
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const num = (v) => (typeof v === "number" ? v.toLocaleString("en-GB") : "—");

const todayIso = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

const humanDate = (iso) => {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const todayUtc = new Date();
    todayUtc.setUTCHours(0, 0, 0, 0);
    const diff = Math.round((todayUtc - dt) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
};

/** One 24-slot chart. Shared by Today (live) and expanded history rows. */
function HourlyChart({ data, compact = false }) {
  const peak = useMemo(() => {
    if (!data?.hours?.length) return 0;
    return data.hours.reduce((m, h) => Math.max(m, h.unique_visitors || 0), 0);
  }, [data]);
  if (!data) return null;
  return (
    <>
      <div className={`flex items-end gap-[2px] ${compact ? "h-16" : "h-24"}`}
           role="img" aria-label={`24-hour visitor chart for ${data.date}`}
           data-testid={`hourly-chart-${data.date}`}>
        {HOURS.map((h) => {
          const bar = data.hours[h] || { unique_visitors: 0, page_views: 0 };
          const heightPct = peak > 0 ? (bar.unique_visitors / peak) * 100 : 0;
          const isPeak = data.busiest_hour === h && bar.unique_visitors > 0;
          return (
            <div
              key={h}
              className="flex-1 flex flex-col justify-end min-w-0 group"
              title={`${String(h).padStart(2, "0")}:00 — ${bar.unique_visitors} unique · ${bar.page_views} views`}
              data-testid={`hourly-bar-${data.date}-${h}`}
            >
              <div
                className={`w-full transition-all rounded-t-sm ${isPeak ? "bg-[#ff4500]" : "bg-[#0e0e0e] group-hover:bg-[#ff4500]"}`}
                style={{ height: `${Math.max(heightPct, bar.unique_visitors > 0 ? 4 : 0)}%` }}
              />
            </div>
          );
        })}
      </div>
      {/* Show EVERY hour label — that was the whole point of the redesign. */}
      <div className="flex gap-[2px] mt-1 mono text-[8px] text-[#888]" aria-hidden="true">
        {HOURS.map((h) => (
          <div key={h} className={`flex-1 text-center ${data.busiest_hour === h ? "text-[#ff4500] font-bold" : ""}`}>
            {String(h).padStart(2, "0")}
          </div>
        ))}
      </div>
      {!compact && (
        <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-12 gap-[2px] mt-2" data-testid={`hourly-cells-${data.date}`}>
          {data.hours.map((bar, h) => (
            <div key={h}
              className={`px-1 py-1 text-center border hairline text-[10px] mono leading-tight ${data.busiest_hour === h && bar.unique_visitors > 0 ? "bg-[#fff4ee] border-[#ff4500]" : "bg-white"}`}
              title={`${bar.page_views} page views`}
            >
              <div className="text-[8px] text-[#888]">{String(h).padStart(2, "0")}</div>
              <div className={`${data.busiest_hour === h && bar.unique_visitors > 0 ? "text-[#ff4500] font-bold" : "text-[#0e0e0e]"}`}>
                {bar.unique_visitors}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Tiny inline sparkline for the condensed history rows. */
function Sparkline({ hourly, busiest }) {
  const peak = Math.max(1, ...hourly);
  return (
    <div className="flex items-end gap-[1px] h-6 w-24" aria-hidden="true">
      {hourly.map((v, h) => (
        <div key={h}
          className={`flex-1 min-w-0 ${busiest === h && v > 0 ? "bg-[#ff4500]" : "bg-[#666]"}`}
          style={{ height: `${Math.max((v / peak) * 100, v > 0 ? 6 : 0)}%` }}
        />
      ))}
    </div>
  );
}

export default function PresenceStatsCard() {
  const [stats, setStats] = useState(null);
  const [statsBusy, setStatsBusy] = useState(false);
  const [today, setToday] = useState(null);
  const [todayBusy, setTodayBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [expanded, setExpanded] = useState({});           // { date: fullHourly | true (loading) }
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyWindow, setHistoryWindow] = useState(7);

  const loadStats = useCallback(async () => {
    setStatsBusy(true);
    try {
      const { data } = await api.get("/admin/presence/stats");
      setStats(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setStatsBusy(false); }
  }, []);

  const loadToday = useCallback(async () => {
    setTodayBusy(true);
    try {
      const { data } = await api.get("/admin/presence/hourly", { params: { date: todayIso() } });
      setToday(data);
    } catch (e) { /* silent — auto-refresh */ }
    finally { setTodayBusy(false); }
  }, []);

  const loadHistory = useCallback(async (n) => {
    setHistoryBusy(true);
    try {
      const { data } = await api.get("/admin/presence/hourly/summary", { params: { days: n } });
      setHistory(data?.rows || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setHistoryBusy(false); }
  }, []);

  // Initial load + auto-refresh loop
  useEffect(() => { loadStats(); loadToday(); loadHistory(historyWindow); }, [loadStats, loadToday, loadHistory, historyWindow]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const a = setInterval(loadStats, 15000);
    const b = setInterval(loadToday, 30000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [autoRefresh, loadStats, loadToday]);

  const toggleExpand = async (date) => {
    if (expanded[date] && expanded[date] !== true) {
      // collapse
      const { [date]: _, ...rest } = expanded;
      setExpanded(rest);
      return;
    }
    setExpanded((prev) => ({ ...prev, [date]: true }));
    try {
      const { data } = await api.get("/admin/presence/hourly", { params: { date } });
      setExpanded((prev) => ({ ...prev, [date]: data }));
    } catch (e) {
      toast.error(formatErr(e));
      setExpanded((prev) => { const { [date]: _, ...rest } = prev; return rest; });
    }
  };

  return (
    <div className="mb-6 p-4 sm:p-5 border hairline bg-white" data-testid="presence-stats-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="eyebrow mb-0.5 flex items-center gap-2">
            <Activity className="w-3 h-3" /> Live activity · admin view
          </div>
          <div className="font-display text-lg leading-tight">Presence dashboard</div>
          <p className="text-xs text-[#666] mt-0.5 max-w-2xl">
            Real numbers, no floor. Today ticks live; previous days condensed below.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10px] mono uppercase tracking-wider text-[#666] inline-flex items-center gap-1.5">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="presence-auto-refresh" />
            auto-refresh
          </label>
          <button
            onClick={() => { loadStats(); loadToday(); loadHistory(historyWindow); }}
            disabled={statsBusy || todayBusy || historyBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider border hairline hover:border-[#ff4500] hover:bg-[#fff4ee] disabled:opacity-50"
            data-testid="presence-refresh"
          >
            {(statsBusy || todayBusy) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </div>

      {!stats ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : (
        <>
          {/* iter188 — tiny legend so 'Unique' vs 'Views' vs 'in / site'
              stops confusing the operator. Hoverable ? icons on desktop,
              always visible on the bucket footer so mobile users can
              still read it. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-[10px] mono text-[#666]" data-testid="presence-legend">
            <span className="inline-flex items-center gap-1" title="Humans = real visitors (crawlers excluded). Bots = search-engine + AI crawlers hitting the sitemap. Signed-in / site: subsets of Humans.">
              <HelpCircle className="w-3 h-3 text-[#888]" strokeWidth={1.75} />
              <span className="uppercase tracking-wider">What am I looking at?</span>
            </span>
            <span><strong className="text-[#0e0e0e]">Humans</strong> = real visitors (bots excluded)</span>
            <span><strong className="text-[#0e0e0e]">Bots</strong> = search / AI crawlers</span>
            <span><strong className="text-[#0e0e0e]">in</strong> = signed-in · <strong className="text-[#0e0e0e]">site</strong> = viewing a campsite</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border hairline mb-4">
            {[
              { key: "now_90s",       label: "Right now",     hint: "Distinct humans pinged in the last 90 s (bots shown separately)." },
              { key: "last_5_min",    label: "Last 5 min",    hint: "Distinct humans in the last 5 min (bots shown separately)." },
              { key: "last_1_hour",   label: "Last 1 hour",   hint: "Sum of the last hourly roll-up. Bots are excluded from the big number." },
              { key: "last_24_hours", label: "Last 24 hours", hint: "Sum of the last 24 hourly roll-ups. Bots excluded — the pattern you saw at 04:00 UTC was Googlebot / GPTBot indexing the sitemap." },
            ].map((b, i) => {
              const d = stats[b.key] || {};
              return (
                <div key={b.key} className={`p-2.5 ${i > 0 ? "border-l hairline" : ""}`} data-testid={`presence-bucket-${b.key}`} title={b.hint}>
                  <div className="text-[9px] mono uppercase tracking-[0.15em] text-[#888] leading-tight flex items-center gap-1">
                    {b.label}
                    <HelpCircle className="w-2.5 h-2.5 text-[#bbb]" strokeWidth={1.75} />
                  </div>
                  <div className="font-display text-2xl text-[#ff4500] leading-none mt-1 mb-1" data-testid={`presence-humans-${b.key}`}>
                    {num(d.active || 0)}
                    <span className="text-[10px] mono text-[#666] uppercase tracking-wider ml-1.5">humans</span>
                  </div>
                  <div className="text-[10px] mono text-[#666] leading-tight">
                    {d.signed_in || 0} in · {d.viewing_a_site || 0} site
                  </div>
                  <div className="text-[10px] mono text-[#999] leading-tight mt-0.5 flex items-center gap-1" title="Search-engine + AI crawlers (Googlebot, Bingbot, GPTBot, ClaudeBot, etc.) — excluded from the big number.">
                    🤖 <span data-testid={`presence-bots-${b.key}`}>{num(d.bots || 0)}</span> bot{(d.bots || 0) === 1 ? "" : "s"}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* === TODAY (live) =================================================== */}
      <div className="border hairline p-3 mb-3 bg-[#fafafa]" data-testid="presence-today-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="eyebrow inline-flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" /> Today · every hour · UTC
          </div>
          <div className="mono text-[10px] uppercase tracking-wider text-[#666] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {todayBusy ? "refreshing…" : (today?.generated_at ? "updated " + new Date(today.generated_at).toLocaleTimeString("en-GB") : "")}
          </div>
        </div>
        {today ? (
          <>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2 mono text-[10px] uppercase tracking-wider text-[#666]">
              <span>
                <span className="text-[#0e0e0e] font-bold">{num(today.total_unique_visitors)}</span> unique ·
                <span className="text-[#0e0e0e] font-bold ml-1">{num(today.total_page_views)}</span> views
              </span>
              {today.busiest_hour !== null && (
                <span className="text-[#ff4500]" data-testid="presence-today-busiest">
                  Busiest {String(today.busiest_hour).padStart(2, "0")}:00 · {num(today.busiest_hour_count)}
                </span>
              )}
            </div>
            <HourlyChart data={today} />
          </>
        ) : (
          <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        )}
      </div>

      {/* === RECENT DAYS (condensed history) =============================== */}
      <div className="border hairline p-3 mb-3 bg-white" data-testid="presence-history-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="eyebrow inline-flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Previous days · click to expand
          </div>
          <label className="inline-flex items-center gap-1.5">
            <span className="mono text-[10px] uppercase tracking-wider text-[#666]">Show</span>
            <select
              value={historyWindow}
              onChange={(e) => setHistoryWindow(parseInt(e.target.value, 10))}
              className="pl-2 pr-6 py-1 text-[11px] mono border hairline bg-white hover:border-[#ff4500] focus:outline-none focus:border-[#ff4500] cursor-pointer"
              data-testid="presence-history-window"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </label>
        </div>
        {historyBusy && history.length === 0 ? (
          <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        ) : (
          <div className="border hairline overflow-hidden">
            {history.length === 0 ? (
              <div className="p-3 text-[11px] mono text-[#888] italic">No previous days recorded yet.</div>
            ) : history.map((row) => {
              const isOpen = Boolean(expanded[row.date]);
              const loadingDetail = expanded[row.date] === true;
              const detail = loadingDetail ? null : expanded[row.date];
              return (
                <div key={row.date} className="border-b hairline last:border-b-0" data-testid={`presence-history-row-${row.date}`}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(row.date)}
                    className="w-full flex items-center gap-3 px-2.5 py-2 hover:bg-[#fafafa] transition-colors text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="text-[#666]">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </span>
                    <div className="min-w-[90px] flex-shrink-0">
                      <div className="text-xs font-bold">{humanDate(row.date)}</div>
                      <div className="mono text-[9px] text-[#888]">{row.date}</div>
                    </div>
                    <Sparkline hourly={row.hourly} busiest={row.busiest_hour} />
                    <div className="flex-1 min-w-0 text-right mono text-[10px]">
                      <span className="text-[#0e0e0e] font-bold">{num(row.total_unique_visitors)}</span>
                      <span className="text-[#888]"> unique · </span>
                      <span className="text-[#0e0e0e] font-bold">{num(row.total_page_views)}</span>
                      <span className="text-[#888]"> views</span>
                      {row.busiest_hour !== null && (
                        <div className="text-[#ff4500] text-[9px] uppercase tracking-wider">
                          Peak {String(row.busiest_hour).padStart(2, "0")}:00 · {row.busiest_hour_count}
                        </div>
                      )}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-2.5 pb-3 pt-1 bg-[#fafafa] border-t hairline">
                      {loadingDetail ? (
                        <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
                      ) : detail ? (
                        <HourlyChart data={detail} />
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {stats && <LiveVisitorsList visitors={stats.live_visitors} />}

      {stats?.top_sites?.length > 0 && (
        <div className="mb-2">
          <div className="eyebrow mb-1.5 inline-flex items-center gap-1.5">
            <Eye className="w-3 h-3" /> Who's viewing what right now
          </div>
          <div className="space-y-2">
            {stats.top_sites.slice(0, 8).map((s) => (
              <div
                key={s.campsite_id}
                className="border hairline bg-white"
                data-testid={`presence-top-site-${s.campsite_id}`}
              >
                <Link
                  to={`/campsites/${s.campsite_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-[#fafafa] border-b hairline hover:bg-[#fff4ee] transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold truncate">{s.name || s.campsite_id}</div>
                    {s.region && <div className="mono text-[9px] text-[#888] uppercase tracking-wider">{s.region}</div>}
                  </div>
                  <div className="mono text-[10px] shrink-0 text-right">
                    <div>
                      <span className="font-bold text-[#ff4500]">{s.human_count ?? s.viewers}</span>
                      <span className="text-[#888] ml-0.5">human{(s.human_count ?? s.viewers) === 1 ? "" : "s"}</span>
                    </div>
                    {s.bot_count > 0 && (
                      <div className="text-[#999]">🤖 {s.bot_count} bot{s.bot_count === 1 ? "" : "s"}</div>
                    )}
                  </div>
                </Link>
                {s.viewer_list?.length > 0 && (
                  <div className="px-2.5 py-1.5 flex flex-wrap gap-1.5">
                    {s.viewer_list.map((v, i) => {
                      const dot =
                        v.kind === "signed_in" ? "bg-[#0a7f3f]"
                        : v.kind === "bot"     ? "bg-[#999]"
                        : "bg-[#c8c8c8]";
                      const isBot = v.kind === "bot";
                      const isSignedIn = v.kind === "signed_in";
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] mono border hairline ${isBot ? "bg-[#f5f5f5] text-[#666]" : isSignedIn ? "bg-[#f4faf6] text-[#0e0e0e]" : "bg-white text-[#666]"}`}
                          title={v.ua_short || ""}
                          data-testid={`presence-viewer-${s.campsite_id}-${i}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                          {isBot ? "🤖" : isSignedIn ? "✓" : "○"}
                          <span className="truncate max-w-[180px]">{v.label}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="pt-2 flex flex-wrap items-center gap-3 text-[9px] mono uppercase tracking-wider text-[#888]">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#0a7f3f]" /> ✓ signed-in</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#c8c8c8]" /> ○ anonymous</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#999]" /> 🤖 crawler</span>
          </div>
        </div>
      )}

      <div className="pt-2 flex items-center gap-1.5 text-[9px] mono text-[#888]" data-testid="presence-compliance-note">
        <Lock className="w-2.5 h-2.5" />
        <span>Public ticker = real numbers. UK CPR 2008 + DMCC 2024 compliant.</span>
      </div>
    </div>
  );
}
